import { NmLoader } from '../../components/NmLoader';
// @refresh reset
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { NmButton } from '../../components/NmButton';
import { NmInput } from '../../components/NmInput';
import { NmSegmentControl } from '../../components/NmSegmentControl';
import { athleteSettingsApi, type AthleteDetail } from '../../services/athleteSettingsApi';
import { calendar, type AthleteItem, workouts } from '../../services/api';
import { formatApiDateCompact, formatIsoDateForDisplay, extractIsoDatePart } from '../../utils/calendarPickerLocale';
import { downloadTablePng } from '../../utils/exportTablePng';
import { useChartLandscapeFullscreen } from '../../hooks/useChartLandscapeFullscreen';
import { BatchComparisonPdcChart } from './BatchComparisonPdcChart';
import { BatchMatrixLandscapeOverlay } from './BatchMatrixLandscapeOverlay';

type BatchMetricKey =
  | 'p5s' | 'p5skg'
  | 'p30s' | 'p30skg'
  | 'p1m' | 'p1mkg'
  | 'p5m' | 'p5mkg'
  | 'p20m' | 'p20mkg'
  | 'mhr' | 'avgHr';
type BatchSortKey = 'athlete' | 'date' | 'weightKg' | BatchMetricKey;

interface WorkoutSummaryLite {
  id: number;
  userId: number;
  athleteName: string;
  title: string;
  workout_day: string;
}

interface BatchRow {
  workoutId: number;
  userId: number;
  athleteName: string;
  title: string;
  workoutDay: string;
  weightKg: number | null;
  p5s: number | null;
  p5skg: number | null;
  p30s: number | null;
  p30skg: number | null;
  p1m: number | null;
  p1mkg: number | null;
  p5m: number | null;
  p5mkg: number | null;
  p20m: number | null;
  p20mkg: number | null;
  mhr: number | null;
  avgHr: number | null;
}

const MAX_SELECTION = 10;
const WORKOUT_FALLBACK_TITLE = 'Workout';
const DATE_RANGE_PRESETS = [7, 14, 30, 90, 180, 365] as const;
type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number] | 'custom';

function workoutTitle(title: string | undefined): string {
  return title?.trim() ? title : WORKOUT_FALLBACK_TITLE;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function batchRangeLabel(t: TFunction, days: DateRangePreset): string {
  if (days === 'custom') return t('charts.rangeCustom');
  const map: Record<number, string> = {
    7: 'charts.range7',
    14: 'charts.range14',
    30: 'charts.range30',
    90: 'charts.range90',
    180: 'charts.range180',
    365: 'charts.range365',
  };
  return t(map[days] ?? 'charts.range90');
}

function detectRangePreset(start: string, end: string): DateRangePreset {
  const today = isoToday();
  if (end !== today) return 'custom';
  for (const days of [...DATE_RANGE_PRESETS].reverse()) {
    if (start === isoDaysAgo(days)) return days;
  }
  return 'custom';
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function asNumber(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function peakValue(peaks: Record<string, unknown> | null | undefined, key: string): number | null {
  if (!peaks) return null;
  const row = peaks[key] as { value?: unknown; wkg?: unknown } | undefined;
  if (!row) return null;
  return asNumber(row.value);
}

function powerPerKg(powerW: number | null, weightKg: number | null): number | null {
  if (powerW == null || weightKg == null || weightKg <= 0) return null;
  return powerW / weightKg;
}

function formatWeightKg(value: number | null): string {
  if (value == null) return '—';
  return value.toFixed(1);
}

function formatMetricCell(key: BatchMetricKey, value: number | null): string {
  if (value == null) return '—';
  if (key.endsWith('kg')) return value.toFixed(2);
  return String(Math.round(value));
}

function athleteWeightKg(detail: AthleteDetail): number | null {
  const latest = asNumber(detail.latest_weight);
  if (latest != null && latest > 0) return latest;
  const setting = detail.setting as { weight?: unknown } | null | undefined;
  const fromSetting = asNumber(setting?.weight);
  if (fromSetting != null && fromSetting > 0) return fromSetting;
  return null;
}

/** Laravel JsonResource may nest the payload under `data` when serialized in some responses. */
function unwrapAthleteDetail(raw: unknown): AthleteDetail | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.id === 'number') return obj as unknown as AthleteDetail;
  const inner = obj.data;
  if (inner && typeof inner === 'object' && typeof (inner as { id?: unknown }).id === 'number') {
    return inner as unknown as AthleteDetail;
  }
  return null;
}

function isWeightMetricName(name: unknown): boolean {
  return typeof name === 'string' && name.toLowerCase() === 'weight';
}

function collectCalendarWeights(
  athleteId: number,
  days: Array<{ day?: string; userMetrics?: Array<{ metric_type_name?: string; value?: unknown }> }>,
  into: Map<string, number>,
): void {
  for (const day of days) {
    const dayKey = day.day ?? '';
    if (!dayKey) continue;
    for (const metric of day.userMetrics ?? []) {
      if (!isWeightMetricName(metric.metric_type_name)) continue;
      const w = asNumber(metric.value);
      if (w != null && w > 0) {
        into.set(`${athleteId}|${dayKey}`, w);
      }
    }
  }
}

function resolveWeightKg(
  userId: number,
  workoutDay: string,
  profileByUser: Map<number, number>,
  byUserDate: Map<string, number>,
): number | null {
  const exact = byUserDate.get(`${userId}|${workoutDay}`);
  if (exact != null) return exact;

  let bestDay = '';
  let bestWeight: number | null = null;
  for (const [key, weight] of byUserDate) {
    const sep = key.indexOf('|');
    if (sep < 0) continue;
    const uid = Number(key.slice(0, sep));
    const day = key.slice(sep + 1);
    if (uid !== userId || !day || day > workoutDay) continue;
    if (day >= bestDay) {
      bestDay = day;
      bestWeight = weight;
    }
  }
  if (bestWeight != null) return bestWeight;

  return profileByUser.get(userId) ?? null;
}

const MATRIX_COLUMNS: Array<{ key: BatchSortKey; label: string; align?: 'start' | 'center' }> = [
  { key: 'athlete', label: 'Athlete', align: 'start' },
  { key: 'weightKg', label: 'Weight (kg)' },
  { key: 'p5s', label: '5s' },
  { key: 'p5skg', label: '5s/kg' },
  { key: 'p30s', label: '30s' },
  { key: 'p30skg', label: '30s/kg' },
  { key: 'p1m', label: '1m' },
  { key: 'p1mkg', label: '1m/kg' },
  { key: 'p5m', label: '5m' },
  { key: 'p5mkg', label: '5m/kg' },
  { key: 'p20m', label: '20m' },
  { key: 'p20mkg', label: '20m/kg' },
  { key: 'mhr', label: 'MHR' },
  { key: 'avgHr', label: 'Avg HR' },
  { key: 'date', label: 'Date', align: 'center' },
];

/** Single source for matrix cell text — table body. */
function batchRowCellValue(row: BatchRow, key: BatchSortKey, locale: string): string {
  switch (key) {
    case 'athlete':
      return row.athleteName;
    case 'date':
      return formatApiDateCompact(row.workoutDay, locale);
    case 'weightKg':
      return formatWeightKg(row.weightKg);
    case 'p5s':
      return formatMetricCell('p5s', row.p5s);
    case 'p5skg':
      return formatMetricCell('p5skg', row.p5skg);
    case 'p30s':
      return formatMetricCell('p30s', row.p30s);
    case 'p30skg':
      return formatMetricCell('p30skg', row.p30skg);
    case 'p1m':
      return formatMetricCell('p1m', row.p1m);
    case 'p1mkg':
      return formatMetricCell('p1mkg', row.p1mkg);
    case 'p5m':
      return formatMetricCell('p5m', row.p5m);
    case 'p5mkg':
      return formatMetricCell('p5mkg', row.p5mkg);
    case 'p20m':
      return formatMetricCell('p20m', row.p20m);
    case 'p20mkg':
      return formatMetricCell('p20mkg', row.p20mkg);
    case 'mhr':
      return formatMetricCell('mhr', row.mhr);
    case 'avgHr':
      return formatMetricCell('avgHr', row.avgHr);
    default:
      return '—';
  }
}

function batchRowToCells(row: BatchRow, locale: string): string[] {
  return MATRIX_COLUMNS.map(col => {
    if (col.key === 'date') {
      const iso = extractIsoDatePart(row.workoutDay);
      return iso ? formatApiDateCompact(iso, locale) : row.workoutDay;
    }
    return batchRowCellValue(row, col.key, locale);
  });
}

function matrixCellClassName(key: BatchSortKey): string | undefined {
  if (key === 'athlete') return 'cmp-batch__matrix-athlete-col';
  if (key === 'date' || key === 'weightKg') return 'ltr-island';
  return undefined;
}

interface PeakCacheEntry {
  selected: WorkoutSummaryLite;
  extended: Record<string, unknown> | null;
}

interface CalendarCacheEntry {
  key: string;
  data: WorkoutSummaryLite[];
  weights: Map<string, number>;
}

function parseExtendedWorkoutDetails(apiBody: unknown): Record<string, unknown> | null {
  if (!apiBody || typeof apiBody !== 'object') return null;
  const root = apiBody as Record<string, unknown>;
  const envelope = root.data;
  if (envelope && typeof envelope === 'object') {
    const env = envelope as Record<string, unknown>;
    const nested = env.workoutDetails ?? env.data ?? env;
    if (nested && typeof nested === 'object') return nested as Record<string, unknown>;
  }
  const direct = root.workoutDetails ?? root;
  return direct && typeof direct === 'object' ? (direct as Record<string, unknown>) : null;
}

function buildBatchRow(
  selected: WorkoutSummaryLite,
  extended: Record<string, unknown> | null,
  weightKg: number | null,
): BatchRow {
  const peaks = (extended?.peaks ?? null) as Record<string, unknown> | null;
  const pw = (peaks?.pw_peak ?? null) as Record<string, unknown> | null;
  const hr = (peaks?.hr_peak ?? null) as Record<string, unknown> | null;
  const p5s = peakValue(pw, 'power5sec');
  const p30s = peakValue(pw, 'power30sec');
  const p1m = peakValue(pw, 'power1min');
  const p5m = peakValue(pw, 'power5min');
  const p20m = peakValue(pw, 'power20min');
  return {
    workoutId: selected.id,
    userId: selected.userId,
    athleteName: selected.athleteName,
    title: workoutTitle(selected.title),
    workoutDay: selected.workout_day ?? '',
    weightKg,
    p5s,
    p5skg: powerPerKg(p5s, weightKg),
    p30s,
    p30skg: powerPerKg(p30s, weightKg),
    p1m,
    p1mkg: powerPerKg(p1m, weightKg),
    p5m,
    p5mkg: powerPerKg(p5m, weightKg),
    p20m,
    p20mkg: powerPerKg(p20m, weightKg),
    mhr: peakValue(hr, 'heart_rate5sec'),
    avgHr: peakValue(hr, 'heart_rate20min'),
  };
}

interface BatchComparePanelProps {
  isCoach: boolean;
  athletes: AthleteItem[];
  fallbackUserId: number | null;
  /** Sync coach athlete filter with header athlete switcher. */
  initialAthleteId?: number | null;
  /** Collapse workout picker — legacy; picker stays expanded when false. */
  embedded?: boolean;
}

function resolveInitialAthleteFilter(
  isCoach: boolean,
  athletes: AthleteItem[],
  fallbackUserId: number | null,
  initialAthleteId?: number | null,
): string {
  if (!isCoach) return String(fallbackUserId ?? '');
  if (initialAthleteId != null && athletes.some(a => a.id === initialAthleteId)) {
    return String(initialAthleteId);
  }
  return athletes[0]?.id ? String(athletes[0].id) : 'all';
}

export function BatchComparePanel({
  isCoach,
  athletes,
  fallbackUserId,
  initialAthleteId = null,
  embedded = false,
}: BatchComparePanelProps) {
  const { t, i18n } = useTranslation();
  const [athleteFilter, setAthleteFilter] = useState<string>(() =>
    resolveInitialAthleteFilter(isCoach, athletes, fallbackUserId, initialAthleteId),
  );
  const [rangePreset, setRangePreset] = useState<DateRangePreset>(90);
  const [startDate, setStartDate] = useState<string>(isoDaysAgo(90));
  const [endDate, setEndDate] = useState<string>(isoToday());
  const [search, setSearch] = useState('');
  const athleteScrollRef = useRef<HTMLDivElement | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState(false);
  const [candidates, setCandidates] = useState<WorkoutSummaryLite[]>([]);
  const [selectedWorkouts, setSelectedWorkouts] = useState<WorkoutSummaryLite[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState(false);
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [sort, setSort] = useState<{ key: BatchSortKey; dir: 'asc' | 'desc' }>({ key: 'p20mkg', dir: 'desc' });
  const [exportingPng, setExportingPng] = useState(false);
  const [athleteWeightById, setAthleteWeightById] = useState<Map<number, number>>(new Map());
  const [weightByUserDate, setWeightByUserDate] = useState<Map<string, number>>(new Map());
  const calendarCacheRef = useRef<CalendarCacheEntry | null>(null);
  const peaksCacheRef = useRef<Map<number, PeakCacheEntry>>(new Map());
  const hadCandidatesRef = useRef(false);

  const athleteIdsKey = useMemo(
    () => athletes.map(a => a.id).sort((a, b) => a - b).join(','),
    [athletes],
  );

  const athleteNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const athlete of athletes) {
      map.set(athlete.id, athlete.full_name);
    }
    return map;
  }, [athleteIdsKey, athletes]);

  const calendarFetchKey = useMemo(() => {
    const ids =
      isCoach
        ? athleteFilter === 'all'
          ? athleteIdsKey.split(',').filter(Boolean).map(Number)
          : [Number(athleteFilter)].filter(Boolean)
        : [fallbackUserId].filter((v): v is number => typeof v === 'number');
    return `${ids.join(',')}|${startDate}|${endDate}`;
  }, [athleteFilter, athleteIdsKey, endDate, fallbackUserId, isCoach, startDate]);

  const selectedIdSet = useMemo(
    () => new Set(selectedWorkouts.map(w => w.id)),
    [selectedWorkouts],
  );

  useEffect(() => {
    if (!isCoach) return;
    const next = resolveInitialAthleteFilter(isCoach, athletes, fallbackUserId, initialAthleteId);
    setAthleteFilter(next);
  }, [athletes, fallbackUserId, initialAthleteId, isCoach]);

  useEffect(() => {
    const active = athleteScrollRef.current?.querySelector('.cmp-batch__athlete-chip--active');
    active?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [athleteFilter, athletes.length]);

  const applyRangePreset = useCallback((preset: DateRangePreset) => {
    setRangePreset(preset);
    if (preset === 'custom') return;
    setStartDate(isoDaysAgo(preset));
    setEndDate(isoToday());
  }, []);

  const onStartDateChange = useCallback((value: string) => {
    setStartDate(value);
    setRangePreset(detectRangePreset(value, endDate));
  }, [endDate]);

  const onEndDateChange = useCallback((value: string) => {
    setEndDate(value);
    setRangePreset(detectRangePreset(startDate, value));
  }, [startDate]);

  const rangeOptions = useMemo(
    () => [
      ...DATE_RANGE_PRESETS.map(days => ({
        value: days as DateRangePreset,
        label: batchRangeLabel(t, days),
      })),
      { value: 'custom' as const, label: batchRangeLabel(t, 'custom') },
    ],
    [t],
  );

  const rebuildRowsFromCache = useCallback((selection: WorkoutSummaryLite[]) => {
    const next = selection
      .map(selected => {
        const cached = peaksCacheRef.current.get(selected.id);
        if (!cached) return null;
        const weightKg = resolveWeightKg(
          selected.userId,
          selected.workout_day,
          athleteWeightById,
          weightByUserDate,
        );
        return buildBatchRow(cached.selected, cached.extended, weightKg);
      })
      .filter((row): row is BatchRow => row != null);
    setRows(next);
  }, [athleteWeightById, weightByUserDate]);

  useEffect(() => {
    const ids = athleteIdsKey
      .split(',')
      .filter(Boolean)
      .map(Number)
      .concat(typeof fallbackUserId === 'number' ? [fallbackUserId] : []);
    const uniqueIds = Array.from(new Set(ids));
    if (!uniqueIds.length) {
      setAthleteWeightById(new Map());
      return;
    }
    let cancelled = false;

    void (async () => {
      const map = new Map<number, number>();

      try {
        const rosterRes = await athleteSettingsApi.getAthletes();
        for (const athlete of rosterRes.data?.data?.athletes ?? []) {
          const detail = unwrapAthleteDetail(athlete) ?? (athlete as AthleteDetail);
          const weight = athleteWeightKg(detail);
          if (weight != null) map.set(detail.id, weight);
        }
      } catch {
        // fall through to per-athlete fetch
      }

      const missing = uniqueIds.filter(id => !map.has(id));
      if (missing.length) {
        const results = await Promise.all(
          missing.map(async (id) => {
            try {
              const res = await athleteSettingsApi.getAthleteDetails(id);
              const detail = unwrapAthleteDetail(res.data?.data?.details);
              const weight = detail ? athleteWeightKg(detail) : null;
              return { id, weight };
            } catch {
              return { id, weight: null as number | null };
            }
          }),
        );
        for (const { id, weight } of results) {
          if (weight != null && weight > 0) map.set(id, weight);
        }
      }

      if (!cancelled) setAthleteWeightById(map);
    })();

    return () => {
      cancelled = true;
    };
  }, [athleteIdsKey, fallbackUserId]);

  useEffect(() => {
    if (!startDate || !endDate || startDate > endDate) {
      setCandidates([]);
      setWeightByUserDate(new Map());
      calendarCacheRef.current = null;
      hadCandidatesRef.current = false;
      return;
    }
    const ids =
      isCoach
        ? athleteFilter === 'all'
          ? athleteIdsKey.split(',').filter(Boolean).map(Number)
          : [Number(athleteFilter)].filter(Boolean)
        : [fallbackUserId].filter((v): v is number => typeof v === 'number');
    if (!ids.length) {
      setCandidates([]);
      return;
    }

    const cached = calendarCacheRef.current;
    if (cached?.key === calendarFetchKey) {
      setCandidates(cached.data);
      setWeightByUserDate(cached.weights);
      setCandidateLoading(false);
      setCandidateError(false);
      return;
    }

    let cancelled = false;
    const showBlockingLoader = !hadCandidatesRef.current;
    if (showBlockingLoader) setCandidateLoading(true);
    setCandidateError(false);

    void Promise.all(ids.map(id => calendar.getCalendarData(id, startDate, endDate)))
      .then(responses => {
        if (cancelled) return;
        const next: WorkoutSummaryLite[] = [];
        const weights = new Map<string, number>();
        responses.forEach((res, idx) => {
          const athleteId = ids[idx];
          const athleteName = athleteNameById.get(athleteId) ?? `#${athleteId}`;
          const days = res.data?.data ?? [];
          collectCalendarWeights(
            athleteId,
            days as Array<{ day?: string; userMetrics?: Array<{ metric_type_name?: string; value?: unknown }> }>,
            weights,
          );
          for (const day of days) {
            for (const w of day.workouts ?? []) {
              if (w.total_time == null || w.id == null) continue;
              next.push({
                id: w.id,
                userId: athleteId,
                athleteName,
                title: workoutTitle(w.title),
                workout_day: w.workout_day ?? day.day ?? '',
              });
            }
          }
        });
        next.sort((a, b) => b.workout_day.localeCompare(a.workout_day));
        calendarCacheRef.current = { key: calendarFetchKey, data: next, weights };
        hadCandidatesRef.current = next.length > 0;
        setCandidates(next);
        setWeightByUserDate(weights);
      })
      .catch(() => {
        if (!cancelled) {
          setCandidateError(true);
          if (showBlockingLoader) {
            setCandidates([]);
            hadCandidatesRef.current = false;
          }
        }
      })
      .finally(() => {
        if (!cancelled) setCandidateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [athleteFilter, athleteIdsKey, athleteNameById, calendarFetchKey, endDate, fallbackUserId, isCoach, startDate]);

  useEffect(() => {
    if (!selectedWorkouts.length) {
      setRows([]);
      setRowsLoading(false);
      setRowsError(false);
      return;
    }

    const missing = selectedWorkouts.filter(w => !peaksCacheRef.current.has(w.id));
    if (!missing.length) {
      rebuildRowsFromCache(selectedWorkouts);
      return;
    }

    let cancelled = false;
    setRowsLoading(true);
    setRowsError(false);

    void Promise.all(
      missing.map(async (selected) => {
        const detailsRes = await workouts.getWorkoutDetails(selected.id).catch(() => null);
        return {
          selected,
          extended: parseExtendedWorkoutDetails(detailsRes?.data),
        };
      }),
    )
      .then(results => {
        if (cancelled) return;
        for (const entry of results) {
          peaksCacheRef.current.set(entry.selected.id, entry);
        }
        rebuildRowsFromCache(selectedWorkouts);
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setRowsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setRowsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rebuildRowsFromCache, selectedWorkouts]);

  useEffect(() => {
    if (!selectedWorkouts.length) return;
    if (selectedWorkouts.every(w => peaksCacheRef.current.has(w.id))) {
      rebuildRowsFromCache(selectedWorkouts);
    }
  }, [athleteWeightById, weightByUserDate, rebuildRowsFromCache, selectedWorkouts]);

  const filteredCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = candidates;
    if (showSelectedOnly) {
      list = list.filter(c => selectedIdSet.has(c.id));
    }
    if (!q) return list;
    return list.filter(c =>
      c.athleteName.toLowerCase().includes(q) ||
      c.title.toLowerCase().includes(q) ||
      c.workout_day.includes(q),
    );
  }, [candidates, search, selectedIdSet, showSelectedOnly]);

  const selectionFull = selectedWorkouts.length >= MAX_SELECTION;

  const sortedRows = useMemo(() => {
    const next = [...rows];
    next.sort((a, b) => {
      if (sort.key === 'athlete') {
        const cmp = a.athleteName.localeCompare(b.athleteName, i18n.language);
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      if (sort.key === 'date') {
        const cmp = a.workoutDay.localeCompare(b.workoutDay);
        return sort.dir === 'asc' ? cmp : -cmp;
      }
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av - bv;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return next;
  }, [i18n.language, rows, sort]);

  const batchPdcSources = useMemo(
    () =>
      selectedWorkouts.map(w => {
        const cached = peaksCacheRef.current.get(w.id);
        const row = rows.find(r => r.workoutId === w.id);
        return {
          workoutId: w.id,
          athleteName: w.athleteName,
          workoutDay: w.workout_day,
          extended: cached?.extended ?? null,
          weightKg: row?.weightKg ?? null,
        };
      }),
    [rows, selectedWorkouts],
  );

  const matrixColumnLabels = useMemo(
    () =>
      MATRIX_COLUMNS.map(col => {
        if (col.key === 'athlete') return { ...col, label: t('compare.batch.colAthlete', 'Athlete') };
        if (col.key === 'date') return { ...col, label: t('compare.batch.colDate', 'Date') };
        if (col.key === 'weightKg') return { ...col, label: t('compare.batch.colWeight', 'Weight (kg)') };
        return col;
      }),
    [t],
  );

  const exportMatrixPng = useCallback(async () => {
    if (!sortedRows.length || exportingPng) return;
    setExportingPng(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadTablePng({
        title: t('compare.batch.matrixTitle', 'Batch compare'),
        footer: 'powered by VO2peaks',
        columns: matrixColumnLabels.map(col => ({
          label: col.label,
          align: col.align ?? 'center',
          minWidth:
            col.key === 'date' ? 72 : col.key === 'athlete' ? 96 : col.key === 'weightKg' ? 52 : undefined,
        })),
        rows: sortedRows.map(row => batchRowToCells(row, i18n.language)),
        fileName: `vo2-batch-compare-${stamp}.png`,
        rtl: i18n.dir() === 'rtl',
      });
    } finally {
      setExportingPng(false);
    }
  }, [exportingPng, i18n, matrixColumnLabels, sortedRows, t]);

  const toggleSelect = (workout: WorkoutSummaryLite) => {
    setSelectedWorkouts(prev => {
      if (prev.some(x => x.id === workout.id)) return prev.filter(x => x.id !== workout.id);
      if (prev.length >= MAX_SELECTION) return prev;
      return [...prev, workout];
    });
  };

  const deselectAll = () => {
    setSelectedWorkouts([]);
    setShowSelectedOnly(false);
  };

  const removeFromSelection = (workoutId: number) => {
    setSelectedWorkouts(prev => prev.filter(w => w.id !== workoutId));
  };

  const cycleSort = (key: BatchSortKey) => {
    setSort(prev => {
      if (prev.key !== key) return { key, dir: key === 'athlete' || key === 'date' ? 'asc' : 'desc' };
      return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  const matrixFs = useChartLandscapeFullscreen();

  const renderMatrixTable = () => (
    <table className="cmp-batch__matrix">
      <thead>
        <tr>
          <th className="cmp-batch__matrix-remove-col" aria-label={t('compare.batch.removeRow', 'Remove')} />
          {matrixColumnLabels.map(col => {
            const active = sort.key === col.key;
            return (
              <th
                key={col.key}
                className={matrixCellClassName(col.key)}
                onClick={() => cycleSort(col.key)}
              >
                {col.label}
                {active ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(row => (
          <tr key={row.workoutId}>
            <td className="cmp-batch__matrix-remove-col">
              <button
                type="button"
                className="cmp-batch__icon-remove cmp-batch__icon-remove--table"
                onClick={() => removeFromSelection(row.workoutId)}
                aria-label={t('compare.batch.removeRow', 'Remove from comparison')}
                title={t('compare.batch.removeRow', 'Remove from comparison')}
              >
                ×
              </button>
            </td>
            {matrixColumnLabels.map(col => (
              <td key={col.key} className={matrixCellClassName(col.key)}>
                {batchRowCellValue(row, col.key, i18n.language)}
              </td>
            ))}
          </tr>
        ))}
        {!sortedRows.length && (
          <tr>
            <td colSpan={16} className="cmp-batch__muted-cell">
              {t('compare.batch.noRows', 'Select workouts to populate matrix.')}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  const renderPickerBody = () => (
    <>
      <div className="cmp-batch__picker-toolbar">
        <div className="cmp-batch__picker-toolbar-title">
          <strong>{t('compare.batch.pickWorkouts', 'Pick workouts')}</strong>
          <span className="cmp-batch__count-badge">
            {t('compare.batch.selectedCount', '{{count}} / {{max}} selected', { count: selectedWorkouts.length, max: MAX_SELECTION })}
          </span>
        </div>
        <div className="cmp-batch__picker-toolbar-controls">
          <div className="cmp-batch__picker-search">
            <NmInput
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('compare.batch.searchPlaceholder', 'athlete, workout, date')}
              aria-label={t('compare.batch.search', 'Search')}
            />
          </div>
          <button
            type="button"
            className="cmp-batch__picker-tab cmp-batch__picker-tab--inline"
            onClick={deselectAll}
            disabled={!selectedWorkouts.length}
          >
            {t('compare.batch.deselectAll', 'Deselect all')}
          </button>
        </div>
      </div>
      <div className="cmp-batch__picker-actions cmp-batch__picker-actions--solo">
        <button
          type="button"
          className={`cmp-batch__picker-tab${showSelectedOnly ? ' cmp-batch__picker-tab--active' : ''}`}
          onClick={() => setShowSelectedOnly(v => !v)}
          disabled={!selectedWorkouts.length}
        >
          {t('compare.batch.selectedOnly', 'Selected only')}
        </button>
      </div>
      {selectedWorkouts.length > 0 && (
        <div className="cmp-batch__chip-row">
          {selectedWorkouts.map(w => (
            <button
              key={w.id}
              type="button"
              className="cmp-batch__chip"
              onClick={() => removeFromSelection(w.id)}
              title={t('compare.batch.removeFromSelection', 'Remove from selection')}
            >
              <span className="ltr-island">{formatApiDateCompact(w.workout_day, i18n.language)}</span>
              <span className="cmp-batch__chip-text">{w.athleteName}</span>
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      {candidateLoading && !candidates.length && (
        <p className="cmp-batch__muted"><NmLoader size="sm" inline label={t('compare.batch.loading', 'Loading workouts...')} /></p>
      )}
      {candidateError && !candidates.length && (
        <p className="cmp-batch__muted">{t('compare.batch.error', 'Could not load workouts.')}</p>
      )}
      {candidates.length > 0 && (
        <div className="cmp-batch__candidate-list">
          {filteredCandidates.map(c => {
            const checked = selectedIdSet.has(c.id);
            const disabled = !checked && selectionFull;
            return (
              <div
                key={`${c.userId}-${c.id}`}
                className={`cmp-batch__candidate ${checked ? 'cmp-batch__candidate--on' : ''} ${disabled ? 'cmp-batch__candidate--disabled' : ''}`}
              >
                <label className="cmp-batch__candidate-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleSelect(c)}
                  />
                </label>
                <button
                  type="button"
                  className="cmp-batch__candidate-main-btn"
                  disabled={disabled}
                  onClick={() => toggleSelect(c)}
                >
                  <span className="cmp-batch__candidate-date ltr-island">
                    {formatApiDateCompact(c.workout_day, i18n.language)}
                  </span>
                  <span className="cmp-batch__candidate-text">
                    <strong>{c.athleteName}</strong>
                    <span>{c.title}</span>
                  </span>
                </button>
                {checked ? (
                  <button
                    type="button"
                    className="cmp-batch__icon-remove"
                    onClick={() => removeFromSelection(c.id)}
                    aria-label={t('compare.batch.removeFromList', 'Remove from selection')}
                    title={t('compare.batch.removeFromList', 'Remove from selection')}
                  >
                    ×
                  </button>
                ) : (
                  <span className="cmp-batch__candidate-remove-spacer" aria-hidden />
                )}
              </div>
            );
          })}
          {!filteredCandidates.length && (
            <p className="cmp-batch__muted">{t('compare.batch.emptyFilter', 'No workouts match your filters.')}</p>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className={`cmp-batch${embedded ? ' cmp-batch--embedded' : ''}`}>
      <div className="cmp-batch__filters">
        {isCoach && (
          <div className="cmp-batch__filter-block">
            <span className="cmp-batch__filter-label">{t('compare.batch.athlete', 'Athlete')}</span>
            <div ref={athleteScrollRef} className="cmp-batch__athlete-scroll" role="tablist" aria-label={t('compare.batch.athlete', 'Athlete')}>
              <button
                type="button"
                role="tab"
                aria-selected={athleteFilter === 'all'}
                className={`cmp-batch__athlete-chip${athleteFilter === 'all' ? ' cmp-batch__athlete-chip--active' : ''}`}
                onClick={() => setAthleteFilter('all')}
              >
                {t('compare.batch.allAthletes', 'All athletes')}
              </button>
              {athletes.map(a => (
                <button
                  key={a.id}
                  type="button"
                  role="tab"
                  aria-selected={athleteFilter === String(a.id)}
                  className={`cmp-batch__athlete-chip${athleteFilter === String(a.id) ? ' cmp-batch__athlete-chip--active' : ''}`}
                  onClick={() => setAthleteFilter(String(a.id))}
                  title={a.full_name}
                >
                  {a.full_name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="cmp-batch__filter-block">
          <span className="cmp-batch__filter-label">{t('compare.batch.dateRange', 'Date range')}</span>
          <div className="cmp-batch__range-scroll">
            <NmSegmentControl
              options={rangeOptions}
              value={rangePreset}
              onChange={applyRangePreset}
              accent
            />
          </div>
          {rangePreset === 'custom' && (
            <div className="cmp-batch__dates-row">
              <label className="cmp-batch__date-field">
                <span className="cmp-batch__date-field-label">{t('compare.batch.from', 'From')}</span>
                <input className="cmp-batch__date" type="date" value={startDate} onChange={e => onStartDateChange(e.target.value)} />
              </label>
              <label className="cmp-batch__date-field">
                <span className="cmp-batch__date-field-label">{t('compare.batch.to', 'To')}</span>
                <input className="cmp-batch__date" type="date" value={endDate} onChange={e => onEndDateChange(e.target.value)} />
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="cmp-batch__body">
        {embedded ? (
          <details className="cmp-batch__picker-details" open>
            <summary>{t('compare.batch.pickWorkouts', 'Pick workouts')}</summary>
            <div className="cmp-batch__picker">{renderPickerBody()}</div>
          </details>
        ) : (
          <div className="cmp-batch__picker">{renderPickerBody()}</div>
        )}

        <div className="cmp-batch__matrix-wrap">
          <div className="cmp-batch__matrix-head">
            <strong>{t('compare.batch.matrixTitle', 'Batch compare')}</strong>
            <div className="cmp-batch__matrix-head-actions">
              <NmButton
                variant="neutral"
                size="sm"
                className="cmp-batch__action-btn"
                onClick={matrixFs.enter}
                disabled={!sortedRows.length}
              >
                {t('compare.batch.fullscreen', 'Full screen')}
              </NmButton>
              <NmButton
                variant="neutral"
                size="sm"
                className="cmp-batch__action-btn"
                onClick={() => void exportMatrixPng()}
                disabled={!sortedRows.length || exportingPng}
              >
                {exportingPng
                  ? t('compare.batch.exportingPng', 'Exporting…')
                  : t('compare.batch.exportPng', 'Export PNG')}
              </NmButton>
            </div>
          </div>
          {rowsLoading && <p className="cmp-batch__muted"><NmLoader size="sm" inline label={t('compare.batch.loadingDetails', 'Loading selected workout details...')} /></p>}
          {rowsError && <p className="cmp-batch__muted">{t('compare.batch.errorDetails', 'Could not load selected workout details.')}</p>}
          <div className="cmp-batch__matrix-scroll">
            {!rowsLoading && !rowsError ? renderMatrixTable() : null}
          </div>
        </div>

        {selectedWorkouts.length > 0 && (
          <BatchComparisonPdcChart
            sources={batchPdcSources}
            loading={rowsLoading}
          />
        )}
      </div>

      <BatchMatrixLandscapeOverlay
        open={matrixFs.open}
        overlayRef={matrixFs.overlayRef}
        onClose={matrixFs.exit}
      >
        {renderMatrixTable()}
      </BatchMatrixLandscapeOverlay>
    </div>
  );
}

